import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../helpers/AuthContext';
import { formatCents } from './affiliateForm';
import './Affiliates.css';

function friendlyError(value) {
  const messages = {
    already_attached: 'This seller account already has an affiliate code attached.',
    invalid_code: 'That affiliate code is not available.',
    self_redemption: 'You cannot redeem your own affiliate code.',
  };
  return messages[value] || value || 'Failed to attach affiliate code.';
}

// Discounts are only supported on annual up-front purchases — fixed-price PayPal subscription
// billing never receives a referral discount, even when the code carries a nonzero rate.
function annualDiscountPercent(affiliateCode) {
  const discount = affiliateCode?.discount;
  if (!discount) return 0;
  const bps = discount.byCadence?.annual?.discountPercentBps ?? discount.discountPercentBps ?? 0;
  return Math.round((bps || 0) / 100);
}

export default function AffiliateReferralPanel() {
  const auth = useAuth();
  const [redemption, setRedemption] = useState(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await auth.fetch(`${auth.API_URL}/v1/seller/affiliates/redemption`);
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || 'Failed to load referral details.');
      setRedemption(body.data);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [auth.API_URL, auth.fetch]);

  useEffect(() => {
    load();
  }, [load]);

  const attach = async event => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const response = await auth.fetch(`${auth.API_URL}/v1/seller/affiliates/redemption`, {
        method: 'POST',
        body: JSON.stringify({ code }),
      });
      const body = await response.json();
      if (!response.ok) throw new Error(friendlyError(body.error));
      await load();
      setCode('');
    } catch (attachError) {
      setError(attachError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="affiliate-referral-panel" aria-label="Your referral code">
      <h2>Your referral code</h2>
      {loading && <p>Loading referral details…</p>}
      {error && <div className="flash error" role="alert">{error}</div>}
      {!loading && redemption && (
        <div>
          <p>
            Code <strong>{redemption.affiliateCode?.displayCode || 'Attached'}</strong> is linked
            to this seller account.
          </p>
          <ul>
            <li>Status: <strong>{redemption.status}</strong></li>
            <li>Total plan discounts recorded: <strong>{formatCents(
              redemption.totalDiscountAmount,
            )}</strong></li>
          </ul>
          <p className="help">
            The code’s frozen terms apply only to eligible plan payments after it was attached.
          </p>
          {annualDiscountPercent(redemption.affiliateCode) > 0 && (
            <p className="help">
              This code’s {annualDiscountPercent(redemption.affiliateCode)}% discount applies only
              to annual up-front plan purchases — it does not reduce monthly PayPal subscription
              billing.
            </p>
          )}
        </div>
      )}
      {!loading && !redemption && (
        <form className="affiliate-referral-form" onSubmit={attach}>
          <p>If another Mayhem seller referred you, you can attach their code here.</p>
          <div className="group">
            <label htmlFor="affiliate-referral-code">Referral or discount code</label>
            <input
              id="affiliate-referral-code"
              type="text"
              value={code}
              onChange={event => setCode(event.target.value)}
              minLength="3"
              maxLength="64"
              required
            />
          </div>
          <button className="button secondary" type="submit" disabled={saving}>
            {saving ? 'Attaching…' : 'Attach code'}
          </button>
          <p className="help">
            Attaching a code does not apply retroactively and cannot be changed without support.
            Any discount applies only to annual up-front plan purchases, not monthly subscription
            billing.
          </p>
        </form>
      )}
    </section>
  );
}
